import i18next from 'i18next'
import {type ComponentType, memo, type PropsWithChildren} from 'react'
import {createBrowserRouter, type LoaderFunctionArgs, Navigate, Outlet, replace, RouterProvider} from 'react-router'

import {getLangUrl, getSpotUrl} from '../hooks/useSpotNavigate'
import {getLastSpotSlug} from '../utils/lastSpot'
import {lazyWithReload} from '../utils/lazyWithReload'

import {CurrentSpotIdListener} from './CurrentSpotIdListener'
import {DashboardProgress} from './DashboardProgress'
import {I18n} from './I18n'
import {Loader} from './Loader'
import {StableRouter, useStableParamValue} from './StableRouter'

const AdminBillingPage = lazyWithReload(async () => ({default: (await import('../../pages/AdminBilling/AdminBillingPage')).AdminBillingPage}))
const AdminColorCreate = lazyWithReload(async () => ({default: (await import('../../pages/AdminColor/AdminColorCreate')).AdminColorCreate}))
const AdminColorEdit = lazyWithReload(async () => ({default: (await import('../../pages/AdminColor/AdminColorEdit')).AdminColorEdit}))
const AdminColorList = lazyWithReload(async () => ({default: (await import('../../pages/AdminColor/AdminColorList')).AdminColorList}))
const AdminCommentEdit = lazyWithReload(async () => ({default: (await import('../../pages/AdminComment/AdminCommentEdit')).AdminCommentEdit}))
const AdminCommentList = lazyWithReload(async () => ({default: (await import('../../pages/AdminComment/AdminCommentList')).AdminCommentList}))
const AdminContestCreate = lazyWithReload(async () => ({default: (await import('../../pages/AdminContest/AdminContestCreate')).AdminContestCreate}))
const AdminContestEdit = lazyWithReload(async () => ({default: (await import('../../pages/AdminContest/AdminContestEdit')).AdminContestEdit}))
const AdminContestList = lazyWithReload(async () => ({default: (await import('../../pages/AdminContest/AdminContestList')).AdminContestList}))
const AdminLaneCreate = lazyWithReload(async () => ({default: (await import('../../pages/AdminLane/AdminLaneCreate')).AdminLaneCreate}))
const AdminLaneEdit = lazyWithReload(async () => ({default: (await import('../../pages/AdminLane/AdminLaneEdit')).AdminLaneEdit}))
const AdminLaneList = lazyWithReload(async () => ({default: (await import('../../pages/AdminLane/AdminLaneList')).AdminLaneList}))
const AdminMembershipCreate = lazyWithReload(async () => ({default: (await import('../../pages/AdminMembership/AdminMembershipCreate')).AdminMembershipCreate}))
const AdminMembershipEdit = lazyWithReload(async () => ({default: (await import('../../pages/AdminMembership/AdminMembershipEdit')).AdminMembershipEdit}))
const AdminMembershipList = lazyWithReload(async () => ({default: (await import('../../pages/AdminMembership/AdminMembershipList')).AdminMembershipList}))
const AdminRouteCreate = lazyWithReload(async () => ({default: (await import('../../pages/AdminRoute/AdminRouteCreate')).AdminRouteCreate}))
const AdminRouteEdit = lazyWithReload(async () => ({default: (await import('../../pages/AdminRoute/AdminRouteEdit')).AdminRouteEdit}))
const AdminRouteList = lazyWithReload(async () => ({default: (await import('../../pages/AdminRoute/AdminRouteList')).AdminRouteList}))
const AdminRouteReport = lazyWithReload(async () => ({default: (await import('../../pages/AdminRoute/AdminRouteReport')).AdminRouteReport}))
const AdminSectorCreate = lazyWithReload(async () => ({default: (await import('../../pages/AdminSector/AdminSectorCreate')).AdminSectorCreate}))
const AdminSectorEdit = lazyWithReload(async () => ({default: (await import('../../pages/AdminSector/AdminSectorEdit')).AdminSectorEdit}))
const AdminSectorList = lazyWithReload(async () => ({default: (await import('../../pages/AdminSector/AdminSectorList')).AdminSectorList}))
const AdminSettings = lazyWithReload(async () => ({default: (await import('../../pages/AdminSettings/AdminSettings')).AdminSettings}))
const AdminSettingsRouter = lazyWithReload(async () => ({default: (await import('../../pages/AdminSettings/AdminSettingsRouter')).AdminSettingsRouter}))
const AdminStats = lazyWithReload(async () => ({default: (await import('../../pages/AdminStats/AdminStats')).AdminStats}))
const AppInstall = lazyWithReload(async () => ({default: (await import('../../pages/AppInstall/AppInstall')).AppInstall}))
const AuthLink = lazyWithReload(async () => ({default: (await import('../../pages/Security/AuthLink')).AuthLink}))
const AuthPopup = lazyWithReload(async () => ({default: (await import('../../pages/Security/AuthPopup')).AuthPopup}))
const BillingCallbackRouter = lazyWithReload(async () => ({default: (await import('../../pages/BillingCallback/BillingCallbackRouter')).BillingCallbackRouter}))
const BillingSessionRouter = lazyWithReload(async () => ({default: (await import('../../pages/BillingCallback/BillingSessionRouter')).BillingSessionRouter}))
const ContestCompetitor = lazyWithReload(async () => ({default: (await import('../../pages/ContestCompetitor/ContestCompetitor')).ContestCompetitor}))
const ContestJudge = lazyWithReload(async () => ({default: (await import('../../pages/ContestJudge/ContestJudge')).ContestJudge}))
const ContestParticipantResult = lazyWithReload(async () => ({default: (await import('../../pages/ContestParticipantResult/ContestParticipantResult')).ContestParticipantResult}))
const ContestResults = lazyWithReload(async () => ({default: (await import('../../pages/ContestResults/ContestResults')).ContestResults}))
const ContestRotation = lazyWithReload(async () => ({default: (await import('../../pages/ContestRotation/ContestRotation')).ContestRotation}))
const ContestRouteResult = lazyWithReload(async () => ({default: (await import('../../pages/ContestRouteResult/ContestRouteResult')).ContestRouteResult}))
const DashboardOutlet = lazyWithReload(async () => ({default: (await import('../../pages/Dashboard/DashboardOutlet')).DashboardOutlet}))
const Landing = lazyWithReload(async () => ({default: (await import('../../pages/Landing/Landing')).Landing}))
const QrParticipantRedirect = lazyWithReload(async () => ({default: (await import('../../pages/QrRedirect/QrParticipantRedirect')).QrParticipantRedirect}))
const QrRouteAssignRedirect = lazyWithReload(async () => ({default: (await import('../../pages/QrRedirect/QrRouteAssignRedirect')).QrRouteAssignRedirect}))
const RouteList = lazyWithReload(async () => ({default: (await import('../../pages/RouteList/RouteList')).RouteList}))
const RouteView = lazyWithReload(async () => ({default: (await import('../../pages/RouteView/RouteView')).RouteView}))
const SpotCreateDialog = lazyWithReload(async () => ({default: (await import('../../pages/SpotCreate/SpotCreateDialog')).SpotCreateDialog}))
const SpotView = lazyWithReload(async () => ({default: (await import('../../pages/SpotView/SpotView')).SpotView}))
const UserProfile = lazyWithReload(async () => ({default: (await import('../../pages/UserProfile/UserProfile')).UserProfile}))
const UserView = lazyWithReload(async () => ({default: (await import('../../pages/UserView/UserView')).UserView}))
const WorkoutCategoryList = lazyWithReload(async () => ({default: (await import('../../pages/WorkoutCategoryList/WorkoutCategoryList')).WorkoutCategoryList}))
const WorkoutList = lazyWithReload(async () => ({default: (await import('../../pages/WorkoutList/WorkoutList')).WorkoutList}))
const WorkoutView = lazyWithReload(async () => ({default: (await import('../../pages/WorkoutView/WorkoutView')).WorkoutView}))
const ErrorHttp = lazyWithReload(async () => ({default: (await import('../../components/Generic/Error/ErrorHttp')).ErrorHttp}))

const NavigateToRoute = memo(function NavigateToRoute() {
  const spotSlug = useStableParamValue('spotSlug')
  const url = spotSlug ? getSpotUrl(spotSlug, '/routes') : '/'

  return <Navigate to={url} replace/>
})

const RouterOutlet = memo(function RouterOutlet() {
  return <StableRouter>
    <CurrentSpotIdListener/>
    <I18n>
      <Outlet/>
    </I18n>
  </StableRouter>
})

const Router404 = memo(function Router404() {
  return <LoaderDashboard>
    <ErrorHttp code={404}/>
  </LoaderDashboard>
})

const DASHBOARD_FALLBACK = <DashboardProgress/>

const LoaderDashboard = memo(function LoaderDashboard({children}: PropsWithChildren) {
  return <Loader fallback={DASHBOARD_FALLBACK} errorMode="display">
    {children}
  </Loader>
})
const LoaderAdmin = memo(function LoaderAdmin({children}: PropsWithChildren) {
  return <Loader fallback={DASHBOARD_FALLBACK} >
    {children}
  </Loader>
})

const withLoader = (WrappedComponent: ComponentType, LoaderComponent: ComponentType<PropsWithChildren>) => {
  return () => <LoaderComponent>
    <WrappedComponent/>
  </LoaderComponent>
}

const workoutConfig = {
  path: 'workouts',
  children: [
    {
      index: true,
      Component: withLoader(WorkoutCategoryList, LoaderDashboard),
    },
    {
      path: ':category',
      children: [{
        index: true,
        Component: withLoader(WorkoutList, LoaderDashboard),
      }, {
        path: ':workoutId',
        Component: withLoader(WorkoutView, LoaderDashboard),
      },
      ],
    },
  ],
}

const profileConfig = {
  path: ':userId',
  children: [
    {
      path: '*',
      Component: withLoader(UserProfile, LoaderDashboard),
    },
  ],
}

let router: ReturnType<typeof createBrowserRouter> | null = null

const i18nInitialized = (): Promise<unknown> => i18next.isInitialized
  ? Promise.resolve()
  : new Promise((resolve) => {
      i18next.on('initialized', resolve)
    })

const landingLoader = async ({request, params}: LoaderFunctionArgs): Promise<Response | null> => {
  const pageLoad = !router?.state.initialized
  const landing = new URL(request.url).pathname.replace(/\/$/, '') === (params['lang'] ? `/${params['lang']}` : '')
  const slug = getLastSpotSlug()
  if (!pageLoad || !landing || !slug) {
    return null
  }

  await i18nInitialized()

  return replace(getSpotUrl(slug, '/routes'))
}

const resumeLoader = async (): Promise<Response> => {
  const slug = getLastSpotSlug()
  await i18nInitialized()

  return replace(slug ? getSpotUrl(slug, '/routes') : getLangUrl('/'))
}

const getRouter = () => router ??= createBrowserRouter([{
  path: '/:lang?/',
  Component: RouterOutlet,
  children: [{
    path: 'auth/oauth/:provider',
    Component: withLoader(AuthPopup, LoaderDashboard),
  }, {
    path: 'spot/:spotSlug/billing/session/*',
    Component: withLoader(BillingSessionRouter, LoaderDashboard),
  }, {
    path: 'qr/a/:assignId',
    Component: withLoader(QrRouteAssignRedirect, LoaderDashboard),
  }, {
    path: 'qr/b/:participantId',
    Component: withLoader(QrParticipantRedirect, LoaderDashboard),
  }, {
    path: '*',
    Component: DashboardOutlet,
    children: [{
      Component: withLoader(Landing, LoaderDashboard),
      loader: landingLoader,
      hydrateFallbackElement: DASHBOARD_FALLBACK,
      children: [{
        index: true,
        element: null,
      }, {
        path: 'spot-create',
        Component: withLoader(SpotCreateDialog, LoaderDashboard),
      }],
    }, {
      path: 'auth/link',
      Component: withLoader(AuthLink, LoaderDashboard),
    }, {
      path: 'profile',
      Component: withLoader(UserView, LoaderDashboard),
      children: [profileConfig],
    },
    workoutConfig, {
      path: 'app',
      Component: withLoader(AppInstall, LoaderDashboard),
    }, {
      path: 'resume',
      loader: resumeLoader,
      hydrateFallbackElement: DASHBOARD_FALLBACK,
    }, {
      path: 'spot/:spotSlug',
      Component: withLoader(SpotView, LoaderDashboard),
      children: [{
        path: 'profile',
        children: [profileConfig],
      },
      workoutConfig, {
        path: 'contests/:contestId/judges',
        Component: withLoader(ContestJudge, LoaderDashboard),
      }, {
        path: 'contests/:contestId/competitor',
        Component: withLoader(ContestCompetitor, LoaderDashboard),
      }, {
        path: 'contests/:contestId/results',
        Component: withLoader(ContestResults, LoaderDashboard),
      }, {
        path: 'contests/:contestId/rotation',
        Component: withLoader(ContestRotation, LoaderDashboard),
      }, {
        path: 'contests/:contestId/participants/:participantId/result',
        Component: withLoader(ContestParticipantResult, LoaderDashboard),
      }, {
        path: 'contests/:contestId/routes/:routeId/result',
        Component: withLoader(ContestRouteResult, LoaderDashboard),
      }, {
        path: 'billing/callback/*',
        Component: withLoader(BillingCallbackRouter, LoaderDashboard),
      }, {
        path: 'routes',
        Component: withLoader(RouteList, LoaderDashboard),
        children: [{
          path: ':routeId',
          Component: withLoader(RouteView, LoaderAdmin),
        }],
      }, {
        path: 'admin',
        children: [{
          path: 'routes',
          Component: withLoader(AdminRouteList, LoaderDashboard),
          children: [{
            path: 'new',
            Component: withLoader(AdminRouteCreate, LoaderAdmin),
          }, {
            path: 'report',
            Component: withLoader(AdminRouteReport, LoaderAdmin),
          }, {
            path: ':routeId',
            Component: withLoader(AdminRouteEdit, LoaderAdmin),
          }],
        }, {
          path: 'comments',
          Component: withLoader(AdminCommentList, LoaderDashboard),
          children: [{
            path: ':commentId',
            Component: withLoader(AdminCommentEdit, LoaderAdmin),
          }],
        }, {
          path: 'contests',
          Component: withLoader(AdminContestList, LoaderDashboard),
          children: [{
            path: 'new',
            Component: withLoader(AdminContestCreate, LoaderAdmin),
          }, {
            path: ':contestId',
            Component: withLoader(AdminContestEdit, LoaderAdmin),
          }],
        }, {
          path: 'stats',
          children: [{
            path: ':x',
            children: [{
              path: ':y',
              Component: withLoader(AdminStats, LoaderDashboard),
            }, {
              path: '*',
              element: <Navigate to="count"/>,
            }],
          }, {
            path: '*',
            element: <Navigate to="grade/count"/>,
          }],
        }, {
          path: 'settings',
          Component: withLoader(AdminSettingsRouter, LoaderDashboard),
          children: [{
            path: 'global',
            Component: withLoader(AdminSettings, LoaderDashboard),
          }, {
            path: 'colors',
            Component: withLoader(AdminColorList, LoaderDashboard),
            children: [{
              path: 'new',
              Component: withLoader(AdminColorCreate, LoaderAdmin),
            }, {
              path: ':colorId',
              Component: withLoader(AdminColorEdit, LoaderAdmin),
            }],
          }, {
            path: 'sectors',
            Component: withLoader(AdminSectorList, LoaderDashboard),
            children: [{
              path: 'new',
              Component: withLoader(AdminSectorCreate, LoaderAdmin),
            }, {
              path: ':sectorId',
              Component: withLoader(AdminSectorEdit, LoaderAdmin),
            }],
          }, {
            path: 'lanes',
            Component: withLoader(AdminLaneList, LoaderDashboard),
            children: [{
              path: 'new',
              Component: withLoader(AdminLaneCreate, LoaderAdmin),
            }, {
              path: ':laneId',
              Component: withLoader(AdminLaneEdit, LoaderAdmin),
            }],
          }, {
            path: 'memberships',
            Component: withLoader(AdminMembershipList, LoaderDashboard),
            children: [{
              path: 'new',
              Component: withLoader(AdminMembershipCreate, LoaderAdmin),
            }, {
              path: ':membershipId',
              Component: withLoader(AdminMembershipEdit, LoaderAdmin),
            }],
          }, {
            path: 'billing',
            Component: withLoader(AdminBillingPage, LoaderDashboard),
          }, {
            path: '*',
            element: <Navigate to="./global" replace/>,
          }],
        }, {
          path: '*',
          Component: NavigateToRoute,
        }],
      }, {
        path: '*',
        Component: NavigateToRoute,
      }],
    }, {
      path: '*',
      element: <Router404/>,
    }],
  }],
}])

export const Router = memo(function Router() {
  return <RouterProvider router={getRouter()} />
})
